/**
 * Homework / Focus Mode (§22.10) — parent control panel.
 *
 * Allow-list based focused-work sessions: parent picks the educational apps
 * that stay open, sets a pomodoro-friendly timer, and starts. While active,
 * we show a live countdown + distractions-blocked counter, surface any
 * in-mode exception requests for approval, and end with a focus report.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, X, BookOpen, Timer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { controlsApi } from '@/api/controls';

function minutesLeft(planned_end_at: string): number {
  return Math.max(0, Math.round((new Date(planned_end_at).getTime() - Date.now()) / 60_000));
}

export function FocusPanel({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const active = useQuery({
    queryKey: ['controls', 'focus-active', childId],
    queryFn: () => controlsApi.activeFocus(childId),
    refetchInterval: 15_000,
  });
  const history = useQuery({
    queryKey: ['controls', 'focus-history', childId],
    queryFn: () => controlsApi.focusHistory(childId, 5),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['controls', 'focus-active', childId] });
    void queryClient.invalidateQueries({ queryKey: ['controls', 'focus-history', childId] });
  };

  if (active.isPending) {
    return (
      <Card>
        <CardContent className="space-y-3 p-6">
          <Skeleton className="h-4 w-2/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4">
      {active.data ? (
        <ActiveSession childId={childId} session={active.data} onChange={invalidate} />
      ) : (
        <StartForm childId={childId} onStarted={invalidate} />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent focus sessions</CardTitle>
          <CardDescription>Completed homework / focus runs.</CardDescription>
        </CardHeader>
        <CardContent>
          {(history.data ?? []).filter((s) => s.status !== 'active').length === 0 ? (
            <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
              No past sessions yet.
            </p>
          ) : (
            <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
              {(history.data ?? [])
                .filter((s) => s.status !== 'active')
                .map((s) => (
                  <li key={s.id} className="flex items-center gap-3 py-2.5 text-sm">
                    <BookOpen className="size-4 shrink-0 text-[color:var(--kz-color-ink-muted)]" />
                    <div className="flex-1 min-w-0">
                      <p className="truncate font-medium">{s.label ?? 'Focus session'}</p>
                      <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                        {new Date(s.started_at).toLocaleString()} · {s.duration_minutes} min planned
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 text-xs">
                      {s.distractions_blocked} blocked
                    </span>
                    <span
                      className={
                        s.status === 'completed'
                          ? 'shrink-0 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800'
                          : 'shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800'
                      }
                    >
                      {s.status}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StartForm({ childId, onStarted }: { childId: string; onStarted: () => void }) {
  const [duration, setDuration] = useState('45');
  const [focusBlock, setFocusBlock] = useState('25');
  const [breakMin, setBreakMin] = useState('5');
  const [allowWeb, setAllowWeb] = useState(true);
  const [apps, setApps] = useState<string[]>([]);
  const [appInput, setAppInput] = useState('');

  const start = useMutation({
    mutationFn: () =>
      controlsApi.startFocus(childId, {
        duration_minutes: parseInt(duration, 10) || 45,
        focus_block_minutes: parseInt(focusBlock, 10) || 25,
        break_minutes: parseInt(breakMin, 10) || 5,
        allow_educational_web: allowWeb,
        allowed_app_ids: apps,
        label: 'Homework',
      }),
    onSuccess: () => {
      toast.success('Focus mode started.');
      onStarted();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Could not start.'),
  });

  const addApp = () => {
    const v = appInput.trim();
    if (v && !apps.includes(v)) setApps([...apps, v]);
    setAppInput('');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Start Homework / Focus Mode</CardTitle>
        <CardDescription>
          Only the apps you allow (plus educational websites, if enabled) stay open. Everything
          else is blocked and counted as a distraction.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="f-duration">Duration (min)</Label>
            <Input id="f-duration" type="number" min={5} max={480} value={duration} onChange={(e) => setDuration(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="f-focus">Focus block</Label>
            <Input id="f-focus" type="number" min={5} max={120} value={focusBlock} onChange={(e) => setFocusBlock(e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="f-break">Break (min)</Label>
            <Input id="f-break" type="number" min={1} max={60} value={breakMin} onChange={(e) => setBreakMin(e.target.value)} />
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label>Allowed educational apps</Label>
          <div className="flex gap-2">
            <Input
              placeholder="com.duolingo / org.khanacademy.android"
              value={appInput}
              onChange={(e) => setAppInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addApp();
                }
              }}
            />
            <Button type="button" variant="outline" size="icon" onClick={addApp} aria-label="Add app">
              <Plus className="size-4" />
            </Button>
          </div>
          {apps.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {apps.map((a) => (
                <span key={a} className="inline-flex items-center gap-1 rounded-full bg-[color:var(--kz-color-surface-sunken)] px-2 py-0.5 font-mono text-xs">
                  {a}
                  <button type="button" onClick={() => setApps(apps.filter((x) => x !== a))} aria-label={`Remove ${a}`}>
                    <X className="size-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between rounded-md border border-[color:var(--kz-color-border-subtle)] p-3">
          <div>
            <p className="text-sm font-medium">Allow educational websites</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">Dictionary, calculator, school sites.</p>
          </div>
          <Switch checked={allowWeb} onCheckedChange={setAllowWeb} aria-label="Allow educational web" />
        </div>

        <Button onClick={() => start.mutate()} disabled={start.isPending}>
          <Timer className="size-4" />
          {start.isPending ? 'Starting…' : 'Start focus session'}
        </Button>
      </CardContent>
    </Card>
  );
}

function ActiveSession({
  childId,
  session,
  onChange,
}: {
  childId: string;
  session: import('@/api/controls').FocusSession;
  onChange: () => void;
}) {
  const queryClient = useQueryClient();
  const left = useMemo(() => minutesLeft(session.planned_end_at), [session.planned_end_at]);

  const exceptions = useQuery({
    queryKey: ['controls', 'focus-exceptions', childId],
    queryFn: () => controlsApi.focusExceptions(childId),
    refetchInterval: 15_000,
  });

  const end = useMutation({
    mutationFn: () => controlsApi.endFocus(childId),
    onSuccess: (report) => {
      toast.success(report.summary);
      onChange();
    },
    onError: () => toast.error('Could not end the session.'),
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      controlsApi.decideFocusException(childId, id, approve),
    onSuccess: (_d, v) => {
      toast.success(v.approve ? 'Exception approved.' : 'Exception denied.');
      void queryClient.invalidateQueries({ queryKey: ['controls', 'focus-exceptions', childId] });
      onChange();
    },
  });

  return (
    <Card className="border-[color:var(--kz-color-brand-primary)]/40 bg-[color:var(--kz-color-brand-primary)]/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Timer className="size-4 text-[color:var(--kz-color-brand-primary)]" />
            Focus mode active
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => end.mutate()} disabled={end.isPending}>
            {end.isPending ? 'Ending…' : 'End now'}
          </Button>
        </div>
        <CardDescription>{session.label ?? 'Homework'} · {session.focus_block_minutes}/{session.break_minutes} pomodoro</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat value={`${left}`} label="min left" />
          <Stat value={`${session.distractions_blocked}`} label="distractions blocked" />
          <Stat value={`${session.allowed_app_ids.length}`} label="apps allowed" />
        </div>

        {(exceptions.data ?? []).length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3">
            <p className="text-sm font-medium text-amber-900">Exception requests</p>
            <ul className="mt-2 space-y-2">
              {(exceptions.data ?? []).map((ex) => (
                <li key={ex.id} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate font-mono text-xs">{ex.label ?? ex.app_id}</span>
                  <Button size="sm" variant="outline" onClick={() => decide.mutate({ id: ex.id, approve: true })} disabled={decide.isPending}>
                    Allow
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: ex.id, approve: false })} disabled={decide.isPending}>
                    Deny
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-card bg-[color:var(--kz-color-surface-card)] p-3 text-center">
      <p className="text-2xl font-semibold text-[color:var(--kz-color-brand-primary)]">{value}</p>
      <p className="text-[11px] text-[color:var(--kz-color-ink-muted)]">{label}</p>
    </div>
  );
}
